import React, {
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import {
  isLayoutMock,
  routes,
  URL,
} from 'appConstants';
import {
  ButtonV4,
  Preloader,
  NoData,
} from 'components';
import { useRequest } from 'hooks';
import {
  _t,
  _t_back,
  dateToUiFormat,
  NEWS_SORT_OPTIONS,
  NewsCategoryFilter,
  NewsSortOrder,
  parseNewsListResponse,
} from 'utils';
import type { GetResponse, News } from 'types';
import { RequestStatus } from 'types';

import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';
import iconCaret from 'assets/img/resolutions-v4/icon-caret-down.svg';
import cardImageFallback from 'assets/img/news-v4/card-bg.png';

import MediaShell from '../MediaShell';
import styles from './styles.module.scss';

const PAGE_SIZE = 16;

const TITLE_MISSION = 'Положение о порядке оказания меры поддержки по проведению бизнес-миссий';
const TITLE_EVENTS = 'Положение о конгрессно-выставочных мероприятиях';
const TITLE_SURVEY = 'Опрос о мерах поддержки «Международная кооперация и экспорт»';
const TITLE_EXPORT = (
  'Объем поддержанного экспорта клиентов платформы «Мой экспорт» ' +
  'в 2024 году превысил 155 млрд рублей'
);

const FALLBACK_NEWS = [
  {
    id: 1,
    slug: 'demo-news',
    pubdate: '16.02.2026',
    title: TITLE_MISSION,
    image: undefined as string | undefined,
  },
  {
    id: 2,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 3,
    slug: '',
    pubdate: '09.04.2025',
    title: TITLE_SURVEY,
    image: cardImageFallback,
  },
  {
    id: 4,
    slug: '',
    pubdate: '06.03.2025',
    title: TITLE_EXPORT,
    image: undefined,
  },
  {
    id: 5,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 6,
    slug: '',
    pubdate: '06.03.2025',
    title: TITLE_EXPORT,
    image: undefined,
  },
  {
    id: 7,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 8,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 9,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 10,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 11,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 12,
    slug: '',
    pubdate: '06.03.2025',
    title: TITLE_EXPORT,
    image: undefined,
  },
  {
    id: 13,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 14,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
  {
    id: 15,
    slug: '',
    pubdate: '06.03.2025',
    title: TITLE_EXPORT,
    image: undefined,
  },
  {
    id: 16,
    slug: '',
    pubdate: '16.02.2026',
    title: TITLE_EVENTS,
    image: undefined,
  },
];

const formatDate = (value: string) => {
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    return value;
  }
  return dateToUiFormat(value);
};

const MediaNews = () => {
  const location = useLocation();
  const { data, request, status } = useRequest<GetResponse<News[]> | News[]>({
    errback: () => undefined,
  });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortOrder, setSortOrder] = useState<NewsSortOrder>('desc');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const layoutMock = isLayoutMock();

  const categoryFilter = useMemo((): NewsCategoryFilter => {
    const param = new URLSearchParams(location.search).get('filter');
    return param === 'news' ? 'news' : 'all';
  }, [location.search]);

  const load = (searchQuery = appliedSearch) => {
    const query = searchQuery.trim();
    request({
      method: 'get',
      url: URL.NEWS.GET,
      params: query ? { search: query } : undefined,
    });
  };

  useEffect(() => {
    if (layoutMock) {
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(0);
  }, [categoryFilter, sortOrder]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    if (layoutMock) {
      return;
    }
    setAppliedSearch(search.trim());
    setPage(0);
    load(search);
  };

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const parsedNews = useMemo(() => parseNewsListResponse(data, {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: appliedSearch,
    sortOrder,
    categoryFilter,
  }), [appliedSearch, categoryFilter, data, page, sortOrder]);

  const apiItems = parsedNews.results;
  const total = parsedNews.count;
  const showFallback = layoutMock || status === RequestStatus.ERROR || (
    status === RequestStatus.SUCCESS && !apiItems.length && !appliedSearch && page === 0
  );

  const activeSort = NEWS_SORT_OPTIONS.find((item) => item.id === sortOrder);

  const cards = showFallback
    ? FALLBACK_NEWS.map((item) => ({
      id: item.id,
      slug: item.slug || `demo-news-${item.id}`,
      pubdate: item.pubdate,
      title: _t(item.title),
      image: item.image,
    }))
    : apiItems.map((item) => ({
      id: item.id,
      slug: item.slug,
      pubdate: item.pubdate,
      title: _t_back(item, 'title'),
      image: item.image,
    }));

  const pageCount = showFallback
    ? 7
    : Math.ceil(total / PAGE_SIZE);

  const pageItems = useMemo(() => {
    if (!pageCount || pageCount <= 1) {
      return [] as Array<number | 'ellipsis'>;
    }
    if (pageCount <= 5) {
      return Array.from({ length: pageCount }, (_, index) => index);
    }
    return [0, 1, 2, 3, 'ellipsis' as const, pageCount - 1];
  }, [pageCount]);

  const sortControl = (
    <div className={styles.sort} ref={sortRef}>
      <span className={styles.sortLabel}>{_t('Сортировать по:')}</span>
      <div className={styles.sortControl}>
        <button
          type="button"
          className={styles.sortBtn}
          aria-expanded={sortOpen}
          onClick={() => setSortOpen((open) => !open)}
        >
          <span>{_t(activeSort?.label || NEWS_SORT_OPTIONS[0].label)}</span>
          <img src={iconCaret} alt="" width={14} height={14} />
        </button>
        {sortOpen ? (
          <ul className={styles.sortMenu}>
            {NEWS_SORT_OPTIONS.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className={option.id === sortOrder
                    ? styles.sortOptionActive
                    : styles.sortOption}
                  onClick={() => {
                    setSortOrder(option.id);
                    setSortOpen(false);
                  }}
                >
                  {_t(option.label)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );

  return (
    <MediaShell
      title="Новости"
      crumbCurrent="Обновления МОСПРОМ"
      showSearch
      searchValue={search}
      searchPlaceholder="Поиск по новостям ..."
      onSearchChange={setSearch}
      onSearchSubmit={onSearch}
      toolbarExtra={sortControl}
    >
      <section className={styles.list}>
        <Preloader isLoading={status === RequestStatus.REQUEST}>
          {cards.length ? (
            <ul className={styles.grid}>
              {cards.map((item, index) => {
                const to = `${routes.media.root}/${item.slug || item.id}`;
                const featured = page === 0 && index === 0;
                const withImage = Boolean(item.image) && !featured;

                return (
                  <li
                    key={item.id}
                    className={cx(
                      styles.card,
                      featured && styles.cardFeatured,
                      withImage && styles.cardImage,
                    )}
                    style={withImage ? {
                      ['--news-photo' as string]: `url(${item.image})`,
                    } : undefined}
                  >
                    <span className={styles.badge}>{_t('НОВОСТИ')}</span>
                    <div className={styles.cardBody}>
                      <time className={styles.date} dateTime={item.pubdate}>
                        {formatDate(item.pubdate)}
                      </time>
                      <h2 className={styles.cardTitle}>{item.title}</h2>
                      <ButtonV4
                        to={to}
                        label="Подробнее"
                        variant="outline"
                        tone={(featured || withImage) ? 'onDark' : 'onLight'}
                        className={styles.detailBtn}
                        iconSrc={iconArrow}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {!showFallback && !apiItems.length && status === RequestStatus.SUCCESS ? (
            <NoData count={0} className={styles.empty} />
          ) : null}
        </Preloader>
      </section>

      {pageCount > 1 ? (
        <nav className={styles.pagination} aria-label={_t('Пагинация')}>
          {pageItems.map((item, index) => {
            if (item === 'ellipsis') {
              const key = `ellipsis-${pageItems[index - 1]}-${pageItems[index + 1]}`;
              return <span key={key} className={styles.pageEllipsis}>...</span>;
            }
            return (
              <button
                key={item}
                type="button"
                className={item === page ? styles.pageBtnActive : styles.pageBtn}
                onClick={() => onPageChange(item)}
                aria-current={item === page ? 'page' : undefined}
              >
                {item + 1}
              </button>
            );
          })}
          {page < pageCount - 1 ? (
            <button
              type="button"
              className={styles.pageNext}
              onClick={() => onPageChange(page + 1)}
              aria-label={_t('Вперёд')}
            >
              <span aria-hidden />
            </button>
          ) : null}
        </nav>
      ) : null}
    </MediaShell>
  );
};

export default MediaNews;
